import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, Thermometer, ClipboardCheck, CalendarDays, Receipt,
  PackageSearch, GraduationCap, UsersRound, KeyRound,
  Settings as SettingsIcon, LogOut,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/fridge", label: "Temperature logs", icon: Thermometer, testid: "nav-fridge" },
  { to: "/audits", label: "H&S audits", icon: ClipboardCheck, testid: "nav-audits" },
  { to: "/events", label: "Events", icon: CalendarDays, testid: "nav-events" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/batches", label: "Batch & expiry", icon: PackageSearch, testid: "nav-batches" },
  { to: "/staff", label: "Staff records", icon: UsersRound, testid: "nav-staff" },
  { to: "/training", label: "Staff training", icon: GraduationCap, testid: "nav-training" },
];

const OWNER_NAV = [
  { to: "/team", label: "Team & access", icon: KeyRound, testid: "nav-team" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

export function SidebarBody({ onNavigate }) {
  const { user, logout } = useAuth();

  return (
    <div className="h-full flex flex-col">
      <div className="px-6 pt-7 pb-8">
        <div className="font-display text-xl font-medium tracking-tight text-[#1c1917] leading-none">
          Hearth<span className="text-[#c0612f]">.</span>
        </div>
        <div className="mt-1 text-[10px] uppercase tracking-[0.28em] text-[#8a8476]">
          Operations console
        </div>
        {user?.business_name && (
          <div className="mt-4 pt-3 border-t border-[#e5e2d9]" data-testid="sidebar-business-name">
            <div className="text-[9px] uppercase tracking-[0.24em] text-[#8a8476]">Workspace</div>
            <div className="text-sm text-[#1b3b36] font-medium truncate mt-0.5" data-testid="sidebar-business-name-value">
              {user.business_name}
            </div>
          </div>
        )}
      </div>
      <nav className="flex-1 px-3 space-y-0.5 no-scrollbar overflow-y-auto">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              data-testid={item.testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-[3px] ${
                  isActive
                    ? "bg-[#1b3b36] text-white"
                    : "text-[#57534e] hover:bg-[#e5e2d9] hover:text-[#1c1917]"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.6} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
        {user?.role === "owner" && (
          <>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#8a8476] px-3 pt-5 pb-2">
              Admin
            </div>
            {OWNER_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={onNavigate}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 text-sm transition-colors rounded-[3px] ${
                      isActive
                        ? "bg-[#1b3b36] text-white"
                        : "text-[#57534e] hover:bg-[#e5e2d9] hover:text-[#1c1917]"
                    }`
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={1.6} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </>
        )}
      </nav>
      <div className="border-t border-[#e5e2d9] p-4 flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-sm text-[#1c1917] truncate" data-testid="sidebar-user-name">
            {user?.name}
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#8a8476]">
            {user?.role}
          </div>
        </div>
        <button
          data-testid="logout-button"
          onClick={logout}
          className="p-2 text-[#57534e] hover:text-[#d94f3d] transition-colors"
          aria-label="Logout"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.6} />
        </button>
      </div>
    </div>
  );
}

export default function Sidebar() {
  return (
    <aside className="flex w-64 shrink-0 bg-[#efece6] border-r border-[#e5e2d9] flex-col" data-testid="app-sidebar">
      <SidebarBody />
    </aside>
  );
}

/**
 * Mobile sidebar — exposed as a sheet that slides in from the left.
 * Manages its own open/close state; the parent only needs to render <MobileSidebar />.
 */
export function MobileSidebar({ open, onOpenChange }) {
  const { pathname } = useLocation();
  // Auto-close on route change so the drawer disappears after the user taps a link.
  useEffect(() => {
    if (open) onOpenChange(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={() => onOpenChange(false)}
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden="true"
      />
      {/* Drawer panel */}
      <aside
        data-testid="mobile-sidebar"
        className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-[#efece6] border-r border-[#e5e2d9] shadow-2xl transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full"}`}
      >
        <SidebarBody onNavigate={() => onOpenChange(false)} />
      </aside>
    </>
  );
}

// Hook so the Layout can drive the drawer state.
export function useMobileSidebar() {
  const [open, setOpen] = useState(false);
  return { open, setOpen };
}
